import {remCalc} from '../../_utils'

interface ThemeTypes {
    borderRadius: number
    colors: {
        primary: string
    }
}

export const DropdownTheme = (theme: ThemeTypes): ThemeTypes => ({
    ...theme,
    borderRadius: 0,
    colors: {...theme.colors, primary: 'black'},
})

export default {
    sizes: {
        small: {
            '.react-select__control': {
                paddingLeft: {
                    base: 'xxxsmall-1.base',
                    md: 'xxxsmall-1.md',
                    lg: 'xxxsmall-1.lg',
                    xl: 'xxxsmall-1.xl',
                },
                paddingRight: {
                    base: 'xxsmall.base',
                    md: 'xxsmall.md',
                    lg: 'xxsmall.lg',
                    xl: 'xxsmall.xl',
                },
                paddingY: 0,
                borderRadius: 'medium-1',
                gap: {
                    base: 'xxxsmall.base',
                    md: 'xxxsmall.md',
                    lg: 'xxxsmall.lg',
                    xl: 'xxxsmall.xl',
                },
                height: 'var(--chakra-sizes-input-sm-height)',
            },
            '.react-select__menu': {
                borderRadius: 'medium-1',
            },
        },
        base: {
            '.react-select__control': {
                paddingLeft: {
                    base: 'xxxsmall-1.base',
                    md: 'xxxsmall-1.md',
                    lg: 'xxxsmall-1.lg',
                    xl: 'xxxsmall-1.xl',
                },
                paddingRight: {
                    base: 'xxsmall.base',
                    md: 'xxsmall.md',
                    lg: 'xxsmall.lg',
                    xl: 'xxsmall.xl',
                },
                paddingY: 0,
                borderRadius: 'large',
                gap: {
                    base: 'xxxsmall-1.base',
                    md: 'xxxsmall-1.md',
                    lg: 'xxxsmall-1.lg',
                    xl: 'xxxsmall-1.xl',
                },
                height: 'var(--chakra-sizes-input-base-height)',
            },
            '.react-select__menu': {
                borderRadius: 'large',
            },
        },
        large: {
            '.react-select__control': {
                paddingLeft: {
                    base: 'xxsmall.base',
                    md: 'xxsmall.md',
                    lg: 'xxsmall.lg',
                    xl: 'xxsmall.xl',
                },
                paddingRight: {
                    base: 'xxsmall-1.base',
                    md: 'xxsmall-1.md',
                    lg: 'xxsmall-1.lg',
                    xl: 'xxsmall-1.xl',
                },
                paddingY: 0,
                borderRadius: 'large',
                gap: {base: 'xxsmall.base', md: 'xxsmall.md', lg: 'xxsmall.lg', xl: 'xxsmall.xl'},
                height: 'var(--chakra-sizes-input-lg-height)',
            },
            '.react-select__menu': {
                borderRadius: 'large',
            },
        },
        xl: {
            '.react-select__control': {
                paddingLeft: {
                    base: 'xxsmall-1.base',
                    md: 'xxsmall-1.md',
                    lg: 'xxsmall-1.lg',
                    xl: 'xxsmall-1.xl',
                },
                paddingRight: {
                    base: 'xxsmall-1.base',
                    md: 'xxsmall-1.md',
                    lg: 'xxsmall-1.lg',
                    xl: 'xxsmall-1.xl',
                },
                paddingY: 0,
                borderRadius: 'large-1',
                gap: {
                    base: 'xxsmall-1.base',
                    md: 'xxsmall-1.md',
                    lg: 'xxsmall-1.lg',
                    xl: 'xxsmall-1.xl',
                },
                height: 'var(--chakra-sizes-input-xl-height)',
            },
            '.react-select__menu': {
                borderRadius: 'large-1',
            },
        },
    },
    baseStyle: {
        container: {
            width: '100%',
            marginLeft: remCalc(3),
            marginRight: remCalc(3),
        },
        '.react-select__control': {
            textStyle: 'body-small',
            fontWeight: 'normal',
            lineHeight: remCalc(18),
            color: 'foreground.neutral.subtle',
            bg: 'background.neutral.default',
            cursor: 'pointer',
            minHeight: 0,
            alignItems: 'center',
            boxShadow:
                '0 0 0 1px var(--chakra-colors-border-neutral-highlight) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 1px var(--chakra-shadows-neutral-xs)',
            border: '1px solid var(--chakra-colors-border-neutral-highlight)',

            _hover: {
                bg: 'background.base.elevated',
            },
        },
        '.react-select__control--is-focused': {
            bg: 'background.base.elevated',
            boxShadow:
                '0 3px 6px 0 var(--chakra-shadows-accent-md), 0 0 0 1px var(--chakra-shadows-accent-xl), 0 0 0 4px var(--chakra-shadows-accent-xs)',

            _hover: {
                border: '1px solid var(--chakra-colors-border-neutral-highlight) !important',
            },
        },
        '.react-select__control:has(.react-select__value-container--has-value)': {
            bg: 'background.base.elevated',
            color: 'foreground.neutral.default',
        },
        '.react-select__control--is-disabled': {
            bg: 'background.base.under !important',
            color: 'foreground.neutral.subtle !important',
            cursor: 'not-allowed !important',
            pointerEvents: 'all',

            _hover: {
                bg: 'background.base.under !important',
                color: 'foreground.neutral.subtle !important',
                cursor: 'not-allowed !important',
            },
        },
        '.react-select__value-container': {
            padding: 0,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            h: '100%',
            w: '100%',
            pointerEvents: 'none',
        },
        '.react-select__placeholder': {
            margin: 0,
            color: 'foreground.neutral.subtle',
        },
        '.react-select__input-container': {
            color: 'foreground.neutral.default',
        },
        '.react-select__option--is-selected:active': {
            backgroundColor: 'background.default',
            color: 'text.default',
        },
        '.react-select__indicators': {
            color: 'foreground.neutral.subtle',
            alignSelf: 'center',
            width: remCalc(12),
            height: remCalc(12),

            svg: {
                width: remCalc(12),
                height: remCalc(12),
            },
        },
        '.react-select__indicator.react-select__dropdown-indicator': {
            padding: 0,
        },
        '.react-select__control--menu-is-open .react-select__indicator': {
            transform: 'rotate(180deg)',
        },
        '.react-select__menu': {
            marginBottom: 0,
            marginTop: {
                base: 'xxxsmall.base',
                md: 'xxxsmall.md',
                lg: 'xxxsmall.lg',
                xl: 'xxxsmall.xl',
            },
            border: '1px solid var(--chakra-colors-border-neutral-highlight)',
            bg: 'background.base.elevated',
            color: 'foreground.neutral.default',
            borderRadius: 'large-1',
            zIndex: 3,
        },
        '.react-select__menu-list': {
            textStyle: 'body-small',
            fontWeight: 'normal',
            lineHeight: remCalc(18),
            padding: {
                base: 'xxxsmall.base',
                md: 'xxxsmall.md',
                lg: 'xxxsmall.lg',
                xl: 'xxxsmall.xl',
            },
            display: 'flex',
            flexDirection: 'column',
            gap: {base: 'micro.base', md: 'micro.md', lg: 'micro.lg', xl: 'micro.xl'},
        },
        '.react-select__option': {
            padding: {base: 'xxsmall.base', md: 'xxsmall.md', lg: 'xxsmall.lg', xl: 'xxsmall.xl'},
            borderRadius: 'large',
            cursor: 'pointer',

            _hover: {
                bg: 'background.base.under',
            },
        },
        '.react-select__option--is-focused': {
            bg: 'background.base.under',
        },
        '.react-select__option--is-selected': {
            bg: 'background.base.under',
        },
        '.react-select__group-heading': {
            borderBottom: 'small',
            borderStyle: 'solid',
            borderColor: 'border.secondary',
            paddingTop: remCalc(4),
            paddingBottom: remCalc(10),
            paddingX: remCalc(10),
            textDecoration: 'underline',
        },
        '.react-select__single-value': {
            marginLeft: 0,
        },
        errorLabel: {
            marginTop: 'xxsmall.lg',
            textStyle: 'body-xxsmall',
            fontWeight: 'normal',
            color: 'foreground.negative.default',
        },
        dropdownIndicator: {
            width: remCalc(16),
            height: remCalc(16),
        },
        placeholderLabel: {
            textStyle: 'body-xxsmall',
            color: 'foreground.neutral.subtle',
            fontWeight: 'regular',
            lineHeight: remCalc(12),
        },
        // groupHeadingWrapper: {
        //     px: '0.75rem',
        //     py: '0.5rem',
        // },
        // groupHeading: {
        //     textStyle: 'xxxsmall-1',
        //     color: 'foreground.neutral.subtle',
        //     textTransform: 'none',
        // },
    },
    variants: {
        invalid: {
            '.react-select__control': {
                bg: 'background.base.elevated',
                color: 'foreground.negative.default',
                boxShadow:
                    '0 3px 6px 0 var(--chakra-shadows-negative-md), 0 0 0 1px var(--chakra-shadows-negative-xl), 0 0 0 4px var(--chakra-shadows-negative-xs)',
            },
        },
        pdpDropdown: {
            container: {
                width: {
                    base: remCalc(168),
                    md: remCalc(351),
                    lg: remCalc(307),
                    xl: remCalc(451),
                },
            },
        },
        productRegistration: {
            // '.react-select__indicator': {
            //     paddingX: remCalc(16),
            // },
            // '.chakra-icon': {
            //     width: remCalc(24),
            //     height: remCalc(24),
            // },
            // '.react-select__value-container': {
            //     px: remCalc(14),
            // },
        },
        dropdownTextStyle: {
            '.react-select__menu-list': {
                textStyle: 'body-xsmall',
                fontWeight: 'normal',
                color: 'text.secondary',
                paddingY: 0,
            },
        },
        orderCancellation: {
            // '.react-select__indicator': {
            //     color: 'text.default',
            // },
            // '.react-select__value-container--has-value': {
            //     px: remCalc(14),
            // },
        },
        warantySelection: {
            '.react-select__control': {
                border: 'unset',
            },
        },
    },
    defaultProps: {size: 'base'},
}
